import { App, Button, Descriptions, Input, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { AdditionalDataTable } from '../../../components/AdditionalDataTable.tsx';
import { RequestStatusRow } from '../../../components/RequestStatusRow.tsx';
import { SHORT_DATETIME_FORMAT } from '../../../utils/dateFormatUtils.ts';
import type { Request } from './useRequestDetailsQuery.tsx';
import { useUpdateRequestCommentMutation } from './useUpdateRequestCommentMutation.tsx';

const AisBlock: FC<{ request: Request }> = ({ request }) => (
  <>
    <p />
    <h3>AIS</h3>
    <Descriptions column={1} size="small" bordered styles={{ label: { width: '200px' } }}>
      <Descriptions.Item label="Hersteller">{request.aisVendorName}</Descriptions.Item>
      {!!request.aisRequestId && (
        <Descriptions.Item label="AIS-Anforderung">
          <Typography.Text copyable code>
            {request.aisRequestId}
          </Typography.Text>
        </Descriptions.Item>
      )}
      {!!request.aisSub && (
        <Descriptions.Item label="AIS-Benutzer">
          <Typography.Text copyable code>
            {request.aisSub}
          </Typography.Text>
        </Descriptions.Item>
      )}
    </Descriptions>
  </>
);

export const RequestTab: FC<{ request: Request; onRefetchRequested: () => void }> = ({ request, onRefetchRequested }) => {
  const { message } = App.useApp();
  const [comment, setComment] = useState(request.comment);
  const [updateRequestCommentMutation] = useUpdateRequestCommentMutation();

  const updateRequestComment = async () => {
    try {
      await updateRequestCommentMutation({
        variables: {
          requestId: request.id,
          comment: comment,
        },
      });
      onRefetchRequested();
    } catch (e) {
      console.log(e);
      message.error('Das Kommentar konnte nicht aktualisiert werden');
    }
  };

  return (
    <>
      <Descriptions column={1} size="small" bordered styles={{ label: { width: '200px' } }}>
        {window._env_.ENV_TAG !== 'prod' && (
          <Descriptions.Item label="Request-UUID">
            <Typography.Text copyable code>
              {request.id}
            </Typography.Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <RequestStatusRow request={request} />
        </Descriptions.Item>
        <Descriptions.Item label="Erstellt am">{dayjs(request.createdAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        <Descriptions.Item label="Erstellt von">
          {request.createdByFirstName} {request.createdByLastName}
        </Descriptions.Item>
        {!!request.modifiedAt && (
          <Descriptions.Item label="Zuletzt bearbeitet am">{dayjs(request.modifiedAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        )}
        {!!(request.modifiedByFirstName || request.modifiedByLastName) && (
          <Descriptions.Item label="Zuletzt bearbeitet von">
            {request.modifiedByFirstName} {request.modifiedByLastName}
          </Descriptions.Item>
        )}
        {!!request.sentAt && (
          <Descriptions.Item label="Gesendet am">{dayjs(request.sentAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        )}
        {!!(request.sentByFirstName || request.sentByLastName) && (
          <Descriptions.Item label="Gesendet von">
            {request.sentByFirstName} {request.sentByLastName}
          </Descriptions.Item>
        )}
        {!!request.sampledAt && (
          <Descriptions.Item label="Probenentnahme am">{dayjs(request.sampledAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        )}
        {!!request.importRequestId && (
          <Descriptions.Item label="Import-ID">
            <Typography.Text copyable code>
              {request.importRequestId}
            </Typography.Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Interner Kommentar">
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Input.TextArea rows={3} value={comment} onChange={e => setComment(e.target.value)} />
            <Button disabled={comment === request.comment} onClick={updateRequestComment}>
              Speichern
            </Button>
          </Space>
        </Descriptions.Item>
      </Descriptions>
      {!!request.aisVendor && <AisBlock request={request} />}
      {!!request.additionalData && Object.keys(request.additionalData).length > 0 && (
        <>
          <p />
          <h3>Zusatzdaten</h3>
          <AdditionalDataTable additionalData={request.additionalData} inlineScroll />
        </>
      )}
    </>
  );
};
